import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { changePassword } from "../../Apis/changePasswordApis";
import { useDispatch } from "react-redux";
import { setAlert } from "../../redux/alertSlice";

const ChangePassword = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const { id: employeeId } = useParams();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [apiError, setApiError] = useState("");

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .min(8, "Must be at least 8 characters")
      .required("Required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Required"),
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        maxWidth: 400,
        mx: "auto",
        mt: 5,
        p: 3,
      }}
    >
      {/* Back Button */}
      <Box sx={{ alignSelf: "flex-start" }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mb: 2, padding: 0 }}>
          <ArrowBack sx={{ color: "black" }} />{" "}
          <Typography variant="body2" sx={{ color: "black" }}>
            Back
          </Typography>
        </IconButton>
      </Box>

      <Typography
        variant="h5"
        fontWeight="bold"
        sx={{ alignSelf: "flex-start" }}
      >
        Create new password
      </Typography>
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{ textAlign: "left", mt: 1, color: "black" }}
      >
        After successfully creating a new password, you will be redirected to
        Login page.
      </Typography>

      <Formik
        initialValues={{ password: "", confirmPassword: "" }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          setApiError(""); // Clear previous errors
          try {
            const response = await changePassword(employeeId, values.password);
            console.log("Password changed successfully:", response);
            localStorage.removeItem("user");
            setIsAuthenticated(false);
            navigate("/mobile/login");

            dispatch(
              setAlert({
                message:
                  "Password details are sent to your registered email address.",
                severity: "success",
              })
            );
          } catch (error) {
            setApiError(error.message || "Failed to update password");
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form style={{ width: "100%", marginTop: 20 }}>
            <Field
              as={TextField}
              type={showPassword ? "text" : "password"}
              name="password"
              label="Password"
              variant="outlined"
              fullWidth
              margin="normal"
              error={touched.password && Boolean(errors.password)}
              helperText={touched.password && errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="caption">
              Must be at least 8 characters.
            </Typography>

            <Field
              as={TextField}
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              label="Confirm Password"
              variant="outlined"
              fullWidth
              margin="normal"
              error={touched.confirmPassword && Boolean(errors.confirmPassword)}
              helperText={touched.confirmPassword && errors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="caption">
              Both passwords must match.
            </Typography>

            {apiError && (
              <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                {apiError}
              </Typography>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSubmitting}
              sx={{ mt: 3 }}
            >
              {isSubmitting ? "Updating..." : "Update Password"}
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ChangePassword;
